// PatientView.js - Patientvy med flikar

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPatient, updatePatient, deletePatient, getPatientPayments, getSaleReceiptPdf, getReceiptPdf, fetchPatientFolkbokforing } from '../Calendar/calendarApi';
import { UserContext } from '../../UserContext';
import { addRecentPatient } from '../../utils/recentPatients';
import { formatPnrPasis, formatAgeFromPnr, isPnrIdType, idTypeLabel } from '../../utils/personnummer';
import CopyPnrButton from '../common/CopyPnrButton';
import { useActivePatient } from '../../patient/ActivePatientContext';
import apiClient from '../../apiClient';
import { getJournalEntries } from './journalApi';
import { getNoShowSummary, noShowCountLabel } from '../Calendar/uteblivetApi';
import JournalTab from './JournalTab';
import POSModal from './POSModal';
import SendSMSModal from './SendSMSModal';
import SendEmailModal from './SendEmailModal';
import SendFormModal from './SendFormModal';
import PortalItemsCard from './PortalItemsCard';
import PatientCommunicationsTab from './PatientCommunicationsTab';
import DocumentsTab from './DocumentsTab';
import './PatientView.css';

// Ändringslogg för personuppgifter (Spår U): bara källa + fältnamn, inga värden.
const CHANGE_FIELD_LABELS = {
  firstname: 'förnamn', lastname: 'efternamn', phone: 'telefon', email: 'e-post',
  street: 'adress', postal_code: 'adress', city: 'adress',
  preferred_name: 'tilltalsnamn', protected_identity: 'skyddade personuppgifter',
  deceased_or_deregistered: 'avregistrering', deregistration_reason: 'avregistrering',
};
const CHANGE_SOURCE_LABELS = {
  bookings_bankid: 'Bokningsappen (BankID)',
  spar: 'folkbokföringen (SPAR)',
  staff: 'personal',
  staff_manual: 'personal (manuell registrering)',
};
// Orsak till manuell registrering vid SPAR-klinik (fältet 'reason:<kod>')
const CHANGE_REASON_LABELS = {
  not_in_spar: 'finns inte i SPAR',
  protected_identity: 'skyddad identitet',
  spar_unavailable: 'SPAR ej nåbart',
  reserve_number: 'reservnummer/utländsk patient',
};

const joinSwedish = (items) => (items.length <= 1
  ? items.join('')
  : `${items.slice(0, -1).join(', ')} och ${items[items.length - 1]}`);

const describeChange = (change) => {
  const all = change.fields || [];
  const via = CHANGE_SOURCE_LABELS[change.source] || change.source;
  const reasonCode = all.find(f => f.startsWith('reason:'))?.slice('reason:'.length);
  const reason = reasonCode ? ` (orsak: ${CHANGE_REASON_LABELS[reasonCode] || reasonCode})` : '';
  const fields = all.filter(f => !f.startsWith('reason:'));
  if (fields.includes('created')) return `Patient skapad via ${via}${reason}`;
  const labels = [...new Set(fields.map(f => CHANGE_FIELD_LABELS[f] || f))];
  const text = joinSwedish(labels) || 'Uppgifter';
  return `${text.charAt(0).toUpperCase()}${text.slice(1)} uppdaterade via ${via}${reason}`;
};

// Namn/adress på SPAR-hämtade patienter är låsta (backend avvisar ändringar)
const SPAR_LOCKED_FIELDS = ['firstname', 'lastname', 'address', 'street', 'postal_code', 'city'];
const SPAR_LOCKED_TEXT = 'Från folkbokföringen — ändras via Uppdatera från SPAR';

const formatChangeTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PatientView = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useContext(UserContext);
  const { setActivePatient } = useActivePatient() || {};

  const [patient, setPatient] = useState(null);
  const [noShowCount, setNoShowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Läs initial flik från URL-parameter (?tab=journal)
  const initialTab = searchParams.get('tab') || 'overview';

  // Från kalenderns högerklick: öppna "Ny journal"-modalen direkt,
  // förkopplad till bokningen (?ny_journal=1&appointment_id=N)
  const [autoOpenNewJournal, setAutoOpenNewJournal] = useState(
    () => searchParams.get('ny_journal') === '1'
  );
  const [appointmentIdForNewJournal] = useState(
    () => searchParams.get('appointment_id') || null
  );
  const [activeTab, setActiveTab] = useState(initialTab);

  // Redigering
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [lookingUpAddress, setLookingUpAddress] = useState(false);

  // Klinik-inställningar
  const [useInternalJournal, setUseInternalJournal] = useState(false);
  // Patientmodulen (formulär via Bokningsappen) — gate:ad på clinics.has_patient_portal
  const [hasPatientPortal, setHasPatientPortal] = useState(false);
  // SPAR-uppslag (folkbokföring) — bara klinik med aktivering
  const [sparAvailable, setSparAvailable] = useState(false);
  const pnrTextRef = useRef(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [portalRefreshKey, setPortalRefreshKey] = useState(0);

  // Journaltidslinje på Översikt-fliken
  const [recentJournals, setRecentJournals] = useState([]);
  const [journalEntryToOpen, setJournalEntryToOpen] = useState(null);

  // POS modal
  const [showPOSModal, setShowPOSModal] = useState(false);

  // SMS modal
  const [showSMSModal, setShowSMSModal] = useState(false);

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Internal note (post-it)
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [internalNote, setInternalNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  // Betalningar
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  // Utskick-fliken laddas om när ett manuellt SMS/e-post/formulär skickats
  const [commsRefreshKey, setCommsRefreshKey] = useState(0);

  // Ladda patient
  useEffect(() => {
    const loadPatient = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPatient(patientId);
        setPatient(data);
        addRecentPatient(currentUser?.user_id, data);
        // Spår Q: patienten blir "aktiv" och följer med i appen tills man
        // stänger journalen (widgeten nere till höger).
        setActivePatient?.(data);
        setEditData({
          firstname: data.firstname,
          lastname: data.lastname,
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          street: data.street || '',
          postal_code: data.postal_code || '',
          city: data.city || ''
        });
        setInternalNote(data.internal_note || '');
      } catch (err) {
        console.error('Error loading patient:', err);
        setError('Kunde inte ladda patient');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      loadPatient();
    }
  }, [patientId]);

  // Uteblivit-räknare (Spår U2) - senaste 12 månaderna, oavsett avgift
  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setNoShowCount(0);
    getNoShowSummary(patientId)
      .then((data) => { if (!cancelled) setNoShowCount(data.no_show_count || 0); })
      .catch((err) => console.error('Kunde inte hämta uteblivna besök:', err));
    return () => { cancelled = true; };
  }, [patientId]);

  // Hämta klinik-inställningar
  useEffect(() => {
    const fetchClinicSettings = async () => {
      try {
        const resp = await apiClient.get('/api/journal/clinic/settings');
        setUseInternalJournal(resp.data.use_internal_journal || false);
        setHasPatientPortal(resp.data.has_patient_portal || false);
        setSparAvailable(!!resp.data.spar_lookup_available);
      } catch (error) {
        setUseInternalJournal(false);
        setHasPatientPortal(false);
        setSparAvailable(false);
      }
    };

    fetchClinicSettings();
  }, []);

  // Uppdatera aktiv flik om URL-parameter ändras
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'journal', 'documents', 'appointments', 'payments', 'communications', 'info'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // Ladda journaltidslinjen (senaste anteckningarna) för Översikt-fliken.
  // Hämtar bara när Översikt faktiskt visas (undviker onödiga anrop vid flikbyten
  // mellan andra flikar); uppdateras när man återvänder till Översikt.
  useEffect(() => {
    if (!useInternalJournal || !patientId || activeTab !== 'overview') return;

    const loadRecentJournals = async () => {
      try {
        const data = await getJournalEntries(patientId, { limit: 5 });
        setRecentJournals(data.entries || []);
      } catch (err) {
        // Tidslinjen är ett komplement - fela tyst
        console.error('Error loading journal timeline:', err);
      }
    };

    loadRecentJournals();
  }, [useInternalJournal, patientId, activeTab]);

  // Öppna en specifik journal från tidslinjen
  const openJournalEntry = (entryId) => {
    setJournalEntryToOpen(entryId);
    setActiveTab('journal');
  };

  // Ladda betalningar
  const loadPayments = async () => {
    if (!patientId) return;

    try {
      setPaymentsLoading(true);
      const data = await getPatientPayments(patientId);
      setPayments(data);
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Ladda betalningar när fliken visas
  useEffect(() => {
    if (activeTab === 'payments') {
      loadPayments();
    }
  }, [activeTab, patientId]);

  // Spara ändringar
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = patient.spar_locked
        ? Object.fromEntries(Object.entries(editData).filter(([k]) => !SPAR_LOCKED_FIELDS.includes(k)))
        : editData;
      const updated = await updatePatient(patientId, payload);
      setPatient(prev => ({ ...prev, ...updated }));
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara ändringar');
    } finally {
      setSaving(false);
    }
  };

  // Uppdatera från SPAR (sparar i backend: namn, tilltalsnamn, adress, skydd,
  // avregistrering). Explicit knapptryck → alltid nytt uppslag (force).
  const handleFetchAddress = async () => {
    try {
      setLookingUpAddress(true);
      setError(null);
      const res = await fetchPatientFolkbokforing(patientId, { force: true });
      const p = res.patient || {};
      setEditData(prev => ({
        ...prev,
        firstname: p.firstname ?? prev.firstname,
        lastname: p.lastname ?? prev.lastname,
        street: p.street || '',
        postal_code: p.postal_code || '',
        city: p.city || ''
      }));
      if (res.patient) {
        // change_log läses om med patienten; behåll övriga fält
        const fresh = await getPatient(patientId).catch(() => null);
        setPatient(prev => ({ ...prev, ...res.patient, ...(fresh ? { change_log: fresh.change_log } : {}) }));
      }
      if (p.protected_identity) {
        toast.warning('Skyddade personuppgifter — ingen adress sparas och inga brev skickas');
      } else {
        toast.success('Uppgifter uppdaterade från SPAR');
      }
    } catch (err) {
      // 503 = ej aktiverat/ingen träff/SPAR nere → be om manuell inmatning
      setError(err.response?.data?.error || 'SPAR-uppslag ej tillgängligt - fyll i manuellt');
    } finally {
      setLookingUpAddress(false);
    }
  };

  // Ta bort patient
  const handleDelete = async () => {
    if (!window.confirm('Är du säker på att du vill ta bort denna patient?')) {
      return;
    }

    try {
      await deletePatient(patientId);
      navigate('/patients');
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte ta bort patient');
    }
  };

  // Spara intern anteckning (post-it)
  const handleSaveNote = async () => {
    try {
      setNoteSaving(true);
      await updatePatient(patientId, { internal_note: internalNote });
      setPatient(prev => ({ ...prev, internal_note: internalNote }));
      setShowNoteModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara anteckning');
    } finally {
      setNoteSaving(false);
    }
  };

  // Formatera datum
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Ladda ner kvitto
  const handleDownloadReceipt = async (payment) => {
    try {
      if (payment.type === 'sale' && payment.sale_id) {
        await getSaleReceiptPdf(payment.sale_id);
      } else {
        await getReceiptPdf(payment.payment_id);
      }
    } catch (err) {
      console.error('Error downloading receipt:', err);
      alert('Kunde inte ladda ner kvitto');
    }
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const labels = {
      scheduled: 'Bokad',
      completed: 'Genomförd',
      cancelled: 'Avbokad',
      no_show: 'Uteblev'
    };
    return <span className={`status-badge status-${status}`}>{labels[status] || status}</span>;
  };

  if (loading) {
    return (
      <div className="patient-view-container">
        <div className="patient-loading">
          <i className="fas fa-spinner fa-spin"></i> Laddar patient...
        </div>
      </div>
    );
  }

  if (error && !patient) {
    return (
      <div className="patient-view-container">
        <div className="patient-error">{error}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/patients')}>
          <i className="fas fa-arrow-left"></i> Tillbaka till patienter
        </button>
      </div>
    );
  }

  if (!patient) return null;

  // "16 mån" under 2 år, annars "N år"; ingen ålder för reservnummer/utländskt id
  const patientAge = formatAgeFromPnr(patient.personnummer, patient.id_type);
  const hasPnr = isPnrIdType(patient.id_type);
  const idLabel = idTypeLabel(patient.id_type);
  // SPAR-uppslag bara för svenska personnummer/samordningsnummer
  const sparForPatient = sparAvailable && hasPnr;

  return (
    <div className="patient-view-container">
      {/* Header */}
      <div className="patient-view-header">
        <button className="back-btn" onClick={() => navigate('/patients')}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <div className="patient-title">
          <div className="patient-name-row">
            <h2>{patient.firstname} {patient.lastname}</h2>
            {/* Post-it indikator */}
            <button
              className={`post-it-btn ${patient.internal_note ? 'has-note' : ''}`}
              onClick={() => setShowNoteModal(true)}
              title={patient.internal_note || 'Lägg till intern anteckning'}
            >
              <i className="fas fa-sticky-note"></i>
            </button>
          </div>
          <span className="patient-pnr">
            <span ref={pnrTextRef}>{formatPnrPasis(patient.personnummer, patient.id_type)}</span>
            <CopyPnrButton personnummer={patient.personnummer} idType={patient.id_type} textRef={pnrTextRef} />
            {!hasPnr && <span className="patient-id-type"> ({idLabel})</span>}
            {patientAge !== null && (
              <span className="patient-age"> ({patientAge})</span>
            )}
            {noShowCount > 0 && (
              <span className="patient-no-show-badge" title="Uteblivna besök senaste 12 månaderna">
                <i className="fas fa-user-slash"></i> {noShowCountLabel(noShowCount)}
              </span>
            )}
          </span>
        </div>
        <div className="header-actions">
          <button className="btn btn-sms" onClick={() => setShowSMSModal(true)}>
            <i className="fas fa-sms"></i> SMS
          </button>
          <button className="btn btn-email" onClick={() => setShowEmailModal(true)}>
            <i className="fas fa-envelope"></i> Email
          </button>
          {/* Bokningsappen loggar in med BankID → kräver svenskt personnummer */}
          {hasPatientPortal && hasPnr && (
            <button className="btn btn-form" onClick={() => setShowFormModal(true)} title="Skicka formulär till patienten via Bokningsappen">
              <i className="fas fa-clipboard-list"></i> Skicka formulär
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowPOSModal(true)}>
            <i className="fas fa-shopping-cart"></i> Kassa
          </button>
        </div>
      </div>

      {error && <div className="patient-error">{error}</div>}

      {/* Folkbokföringsmarkeringar (SPAR) */}
      {patient.protected_identity && (
        <div className="patient-spar-flag protected">
          <i className="fas fa-user-shield"></i>
          <strong>Skyddade personuppgifter</strong> — ingen adress, inga brev.
          SMS och e-post fungerar som vanligt.
        </div>
      )}
      {patient.deceased_or_deregistered && (
        <div className="patient-spar-flag deregistered">
          <i className="fas fa-exclamation-triangle"></i>
          <strong>Avregistrerad i folkbokföringen</strong>
          {patient.deregistration_reason ? ` (${patient.deregistration_reason})` : ''} —
          påminnelser, bokningsbekräftelser och kallelser skickas inte.
        </div>
      )}

      {/* Flikar */}
      <div className="patient-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-home"></i> Översikt
        </button>
        {/* Journal-fliken visas endast om kliniken har inbyggd journalmodul aktiverad */}
        {useInternalJournal && (
          <button
            className={`tab ${activeTab === 'journal' ? 'active' : ''}`}
            onClick={() => setActiveTab('journal')}
          >
            <i className="fas fa-file-medical"></i> Journal
          </button>
        )}
        {/* Dokument: samma behörighet som journalen (Spår D) */}
        {useInternalJournal && (
          <button
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            <i className="fas fa-folder-open"></i> Dokument
          </button>
        )}
        <button
          className={`tab ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          <i className="fas fa-calendar"></i> Bokningar
          {patient.appointment_count > 0 && (
            <span className="tab-badge">{patient.appointment_count}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <i className="fas fa-credit-card"></i> Betalningar
        </button>
        <button
          className={`tab ${activeTab === 'communications' ? 'active' : ''}`}
          onClick={() => setActiveTab('communications')}
        >
          <i className="fas fa-paper-plane"></i> Utskick
        </button>
        <button
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <i className="fas fa-user"></i> Personuppgifter
        </button>
      </div>

      {/* Innehåll */}
      <div className="patient-content">
        {/* Översikt */}
        {activeTab === 'overview' && (
          <div className="tab-content overview-tab">
            <div className="overview-grid">
              {/* Kontaktinformation */}
              <div className="overview-card">
                <h4><i className="fas fa-address-card"></i> Kontaktinformation</h4>
                <div className="info-row">
                  <span className="label">Telefon:</span>
                  <span className="value">{patient.phone || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="label">E-post:</span>
                  <span className="value">{patient.email || '-'}</span>
                </div>
                <div className="info-row">
                  <span className="label">Adress:</span>
                  <span className="value">{patient.protected_identity ? 'Skyddade personuppgifter' : (patient.address || '-')}</span>
                </div>
              </div>

              {/* Statistik */}
              <div className="overview-card">
                <h4><i className="fas fa-chart-bar"></i> Statistik</h4>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-value">{patient.appointment_count || 0}</span>
                    <span className="stat-label">Totalt bokningar</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{patient.upcoming_appointments?.length || 0}</span>
                    <span className="stat-label">Kommande</span>
                  </div>
                </div>
                <div className="info-row">
                  <span className="label">Senaste bokning:</span>
                  <span className="value">{formatDate(patient.last_appointment)}</span>
                </div>
              </div>

              {/* Journalhistorik (tidslinje) - endast med inbyggd journalmodul */}
              {useInternalJournal && (
                <div className="overview-card wide">
                  <h4><i className="fas fa-notes-medical"></i> Journalhistorik</h4>
                  {recentJournals.length === 0 ? (
                    <p className="journal-timeline-empty">Inga journalanteckningar ännu.</p>
                  ) : (
                    <div className="journal-timeline">
                      {recentJournals.map(entry => (
                        <button
                          key={entry.entry_id}
                          className="journal-timeline-item"
                          onClick={() => openJournalEntry(entry.entry_id)}
                          title="Öppna i journalfliken"
                        >
                          <div className="journal-timeline-date">
                            <span className="date">{formatDate(entry.created_at)}</span>
                          </div>
                          <div className="journal-timeline-details">
                            <span className="template">{entry.display_name || entry.template_name || 'Utan mall'}</span>
                            <span className="user">{entry.user_name}</span>
                          </div>
                          <span className={`journal-timeline-status status-${entry.status}`}>
                            {entry.status === 'signed' ? 'Signerad' : 'Utkast'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    className="journal-timeline-show-all"
                    onClick={() => setActiveTab('journal')}
                  >
                    Visa alla i journalfliken <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              )}

              {/* Patientportal: skickade formulär + inkomna svar */}
              {hasPatientPortal && (
                <PortalItemsCard
                  patientId={patient.patient_id}
                  refreshKey={portalRefreshKey}
                  onSendClick={() => setShowFormModal(true)}
                />
              )}

              {/* Kommande bokningar */}
              {patient.upcoming_appointments?.length > 0 && (
                <div className="overview-card wide">
                  <h4><i className="fas fa-calendar-alt"></i> Kommande bokningar</h4>
                  <div className="upcoming-list">
                    {patient.upcoming_appointments.map(apt => (
                      <div key={apt.appointment_id} className="upcoming-item">
                        <div className="upcoming-date">
                          <span className="date">{formatDate(apt.start_time)}</span>
                          <span className="time">
                            {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div className="upcoming-details">
                          <span className="type">
                            {apt.is_group_participant ? `Grupp: ${apt.group_name || 'Gruppbokning'}` : (apt.appointment_type?.name || 'Bokning')}
                          </span>
                          <span className="user">{apt.user_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bokningar */}
        {activeTab === 'appointments' && (
          <div className="tab-content appointments-tab">
            <div className="appointments-header">
              <h4>Alla bokningar</h4>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => navigate('/calendar')}
              >
                <i className="fas fa-plus"></i> Ny bokning
              </button>
            </div>

            {patient.appointments?.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calendar-times"></i>
                <p>Inga bokningar registrerade</p>
              </div>
            ) : (
              <table className="appointments-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Tid</th>
                    <th>Typ</th>
                    <th>Behandlare</th>
                    <th>Status</th>
                    <th>Anteckningar</th>
                  </tr>
                </thead>
                <tbody>
                  {patient.appointments?.map(apt => (
                    <tr key={apt.appointment_id}>
                      <td>{formatDate(apt.start_time)}</td>
                      <td>
                        {new Date(apt.start_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                        {' - '}
                        {new Date(apt.end_time).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        {apt.is_group_participant
                          ? `Grupp: ${apt.group_name || 'Gruppbokning'}`
                          : (apt.appointment_type?.name || '-')}
                      </td>
                      <td>{apt.user_name || '-'}</td>
                      <td><StatusBadge status={apt.status} /></td>
                      <td className="notes-cell">{apt.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Journal - visas endast om kliniken har inbyggd journalmodul aktiverad */}
        {activeTab === 'journal' && useInternalJournal && (
          <JournalTab
            patientId={patient.patient_id}
            patient={patient}
            initialEntryId={journalEntryToOpen}
            onInitialEntryConsumed={() => setJournalEntryToOpen(null)}
            autoOpenNewJournal={autoOpenNewJournal}
            onAutoOpenConsumed={() => setAutoOpenNewJournal(false)}
            appointmentIdForNewJournal={appointmentIdForNewJournal}
          />
        )}

        {/* Dokument - uppladdade och skrivna dokument (Spår D) */}
        {activeTab === 'documents' && useInternalJournal && (
          <DocumentsTab patient={patient} />
        )}

        {/* Betalningar */}
        {activeTab === 'payments' && (
          <div className="tab-content payments-tab">
            <div className="payments-header">
              <h4>Betalningshistorik</h4>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowPOSModal(true)}
              >
                <i className="fas fa-plus"></i> Ny betalning
              </button>
            </div>

            {paymentsLoading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i> Laddar betalningar...
              </div>
            ) : payments.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-receipt"></i>
                <p>Inga betalningar registrerade</p>
              </div>
            ) : (
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Kvittonr</th>
                    <th>Typ</th>
                    <th>Beskrivning</th>
                    <th>Belopp</th>
                    <th>Metod</th>
                    <th>Friskvård</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => (
                    <tr key={payment.payment_id} className={payment.is_friskvard ? 'friskvard-row' : ''}>
                      <td>{formatDateTime(payment.payment_date)}</td>
                      <td className="receipt-num">{payment.receipt_number || '-'}</td>
                      <td>
                        {payment.type === 'appointment' ? (
                          <span className="type-badge type-appointment">
                            <i className="fas fa-calendar-check"></i> Bokning
                          </span>
                        ) : (
                          <span className="type-badge type-sale">
                            <i className="fas fa-shopping-cart"></i> Försäljning
                          </span>
                        )}
                      </td>
                      <td className="description-cell">{payment.description || '-'}</td>
                      <td className="amount-cell">{parseFloat(payment.amount).toFixed(2)} kr</td>
                      <td>
                        {payment.payment_method === 'card' && <span className="payment-method"><i className="fas fa-credit-card"></i> Kort</span>}
                        {payment.payment_method === 'cash' && <span className="payment-method"><i className="fas fa-money-bill-wave"></i> Kontant</span>}
                        {payment.payment_method === 'swish' && <span className="payment-method"><i className="fas fa-mobile-alt"></i> Swish</span>}
                        {payment.payment_method === 'frikort' && <span className="payment-method"><i className="fas fa-id-card"></i> Frikort</span>}
                        {payment.payment_method === 'uteblivet' && <span className="payment-method"><i className="fas fa-user-slash"></i> Uteblivet</span>}
                        {payment.payment_method === 'avgiftsfri' && <span className="payment-method"><i className="fas fa-phone"></i> Avgiftsfritt (ej fysiskt besök)</span>}
                      </td>
                      <td>
                        {payment.is_friskvard && (
                          <span className="friskvard-badge">
                            <i className="fas fa-heart"></i> Friskvård
                          </span>
                        )}
                      </td>
                      <td className="actions-cell">
                        {payment.payment_method !== 'avgiftsfri' && (
                          <button
                            className="btn-icon"
                            onClick={() => handleDownloadReceipt(payment)}
                            title="Ladda ner kvitto"
                          >
                            <i className="fas fa-download"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Personuppgifter */}
        {activeTab === 'communications' && (
          <PatientCommunicationsTab patientId={patientId} refreshKey={commsRefreshKey} />
        )}

        {activeTab === 'info' && (
          <div className="tab-content info-tab">
            {editing ? (
              <div className="edit-form">
                {patient.spar_locked ? (
                  <div className="form-group spar-locked">
                    <label>Namn</label>
                    <input type="text" value={`${patient.firstname} ${patient.lastname}`} disabled />
                    <small><i className="fas fa-lock"></i> {SPAR_LOCKED_TEXT}</small>
                  </div>
                ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>Förnamn</label>
                    <input
                      type="text"
                      value={editData.firstname}
                      onChange={(e) => setEditData(prev => ({ ...prev, firstname: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Efternamn</label>
                    <input
                      type="text"
                      value={editData.lastname}
                      onChange={(e) => setEditData(prev => ({ ...prev, lastname: e.target.value }))}
                    />
                  </div>
                </div>
                )}
                <div className="form-group">
                  <label>{idLabel}</label>
                  <input type="text" value={formatPnrPasis(patient.personnummer, patient.id_type)} disabled />
                  <small>{idLabel} kan inte ändras</small>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>E-post</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
                {patient.spar_locked ? (
                  <div className="form-group spar-locked">
                    <label>Postadress</label>
                    <input
                      type="text"
                      value={patient.street
                        ? `${patient.street}, ${[patient.postal_code, patient.city].filter(Boolean).join(' ')}`
                        : 'Ingen adress i folkbokföringen'}
                      disabled
                    />
                    <small><i className="fas fa-lock"></i> {SPAR_LOCKED_TEXT}</small>
                  </div>
                ) : (<>
                {!patient.protected_identity && (
                  <div className="form-group">
                    <label>Adress (fritext)</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => setEditData(prev => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                )}

                {/* Strukturerad postadress för brevkallelser (Ekopost) — aldrig vid skyddad identitet */}
                {patient.protected_identity ? (
                  <div className="form-group">
                    <label>Postadress</label>
                    <small>Skyddade personuppgifter — ingen adress sparas.</small>
                  </div>
                ) : (<>
                <div className="form-group">
                  <label>
                    Postadress (för brevkallelser)
                    {sparForPatient && (
                      <button
                        type="button"
                        className="btn btn-link"
                        style={{ marginLeft: '8px', padding: 0 }}
                        onClick={handleFetchAddress}
                        disabled={lookingUpAddress}
                      >
                        {lookingUpAddress ? 'Hämtar...' : 'Hämta från SPAR'}
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    placeholder="Gatuadress"
                    value={editData.street}
                    onChange={(e) => setEditData(prev => ({ ...prev, street: e.target.value }))}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Postnummer</label>
                    <input
                      type="text"
                      value={editData.postal_code}
                      onChange={(e) => setEditData(prev => ({ ...prev, postal_code: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Ort</label>
                    <input
                      type="text"
                      value={editData.city}
                      onChange={(e) => setEditData(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                </div>
                </>)}
                </>)}

                <div className="form-actions">
                  <button className="btn btn-secondary" onClick={() => setEditing(false)}>
                    Avbryt
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'Sparar...' : 'Spara ändringar'}
                  </button>
                </div>

                <div className="danger-zone">
                  <h4>Riskzon</h4>
                  <p>Att ta bort en patient går endast om patienten inte har några bokningar.</p>
                  <button className="btn btn-danger" onClick={handleDelete}>
                    <i className="fas fa-trash"></i> Ta bort patient
                  </button>
                </div>
              </div>
            ) : (
              <div className="info-display">
                <div className="info-section">
                  <h4>Personuppgifter</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Förnamn</span>
                      <span className="value">{patient.firstname}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Efternamn</span>
                      <span className="value">{patient.lastname}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">{idLabel}</span>
                      <span className="value">{formatPnrPasis(patient.personnummer, patient.id_type)}</span>
                    </div>
                    {patient.preferred_name && (
                      <div className="info-item">
                        <span className="label">Tilltalsnamn</span>
                        <span className="value">{patient.preferred_name}</span>
                      </div>
                    )}
                  </div>
                  {patient.spar_locked && (
                    <p className="spar-locked-note">
                      <i className="fas fa-lock"></i> Namn och adress: {SPAR_LOCKED_TEXT.charAt(0).toLowerCase()}{SPAR_LOCKED_TEXT.slice(1)}
                    </p>
                  )}
                  {patient.change_log && patient.change_log.length > 0 && (
                    <ul className="patient-change-log">
                      {patient.change_log.map(change => (
                        <li key={change.change_id}>
                          {describeChange(change)} {formatChangeTime(change.changed_at)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="info-section">
                  <h4>Kontaktuppgifter</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">E-post</span>
                      <span className="value">{patient.email || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Telefon</span>
                      <span className="value">{patient.phone || '-'}</span>
                    </div>
                    {patient.protected_identity ? (
                      <div className="info-item full-width">
                        <span className="label">Adress</span>
                        <span className="value">Skyddade personuppgifter — ingen adress</span>
                      </div>
                    ) : (<>
                      <div className="info-item full-width">
                        <span className="label">Adress</span>
                        <span className="value">{patient.address || '-'}</span>
                      </div>
                      <div className="info-item full-width">
                        <span className="label">Postadress (brev)</span>
                        <span className="value">
                          {patient.street
                            ? `${patient.street}, ${[patient.postal_code, patient.city].filter(Boolean).join(' ')}`
                            : '-'}
                        </span>
                      </div>
                    </>)}
                  </div>
                </div>

                {(sparForPatient || patient.spar_checked_at) && (
                  <div className="info-section">
                    <h4>Folkbokföring (SPAR)</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Senast kontrollerad</span>
                        <span className="value">
                          {patient.spar_checked_at ? formatChangeTime(patient.spar_checked_at) : 'Aldrig'}
                        </span>
                      </div>
                    </div>
                    {sparForPatient && (
                      <button
                        className="btn btn-secondary"
                        onClick={handleFetchAddress}
                        disabled={lookingUpAddress}
                        title="Hämta namn, tilltalsnamn och adress från folkbokföringen"
                      >
                        <i className="fas fa-sync"></i> {lookingUpAddress ? 'Hämtar...' : 'Uppdatera från SPAR'}
                      </button>
                    )}
                  </div>
                )}

                <div className="info-section">
                  <h4>Systeminfo</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Skapad</span>
                      <span className="value">{formatDateTime(patient.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="info-actions">
                  <button className="btn btn-secondary" onClick={() => setEditing(true)}>
                    <i className="fas fa-edit"></i> Redigera personuppgifter
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* POS Modal */}
      {showPOSModal && (
        <POSModal
          patient={patient}
          onClose={() => setShowPOSModal(false)}
          onSaleComplete={() => {
            // Uppdatera betalningslistan och växla till betalningsfliken
            setActiveTab('payments');
            loadPayments();
          }}
        />
      )}

      {/* SMS Modal */}
      {showSMSModal && (
        <SendSMSModal
          patient={patient}
          onClose={() => setShowSMSModal(false)}
          onSuccess={() => setCommsRefreshKey(k => k + 1)}
        />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <SendEmailModal
          patient={patient}
          onClose={() => setShowEmailModal(false)}
          onSuccess={() => setCommsRefreshKey(k => k + 1)}
        />
      )}

      {/* Skicka formulär (patientmodulen) */}
      {showFormModal && (
        <SendFormModal
          patient={patient}
          onClose={() => setShowFormModal(false)}
          onSent={() => {
            setPortalRefreshKey(k => k + 1);
            setCommsRefreshKey(k => k + 1);
          }}
        />
      )}

      {/* Post-it Modal - Intern anteckning */}
      {showNoteModal && (
        <div className="post-it-modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="post-it-modal" onClick={(e) => e.stopPropagation()}>
            <div className="post-it-modal-header">
              <h3><i className="fas fa-sticky-note"></i> Intern anteckning</h3>
              <button className="close-btn" onClick={() => setShowNoteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="post-it-modal-body">
              <p className="post-it-hint">
                <i className="fas fa-info-circle"></i>
                Denna anteckning är endast synlig för personal och visas aldrig för patienten.
              </p>
              <textarea
                className="post-it-textarea"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="T.ex. &quot;Kommer ofta sent&quot;, &quot;Avbokar i sista minuten&quot;..."
                rows={5}
              />
            </div>
            <div className="post-it-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setInternalNote(patient.internal_note || '');
                  setShowNoteModal(false);
                }}
              >
                Avbryt
              </button>
              <button
                className="btn btn-warning"
                onClick={handleSaveNote}
                disabled={noteSaving}
              >
                {noteSaving ? 'Sparar...' : 'Spara anteckning'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientView;
